import { useMemo, type FC } from "react";
import type {
  GroupBase,
  MenuPosition,
  MenuPlacement,
  StylesConfig
} from "react-select";
import ReactSelect from "react-select";
import { cx } from "../../../utils/classnames";
import { defaultSelectTheme, sizedSelectStyles } from "../Select";
import type { InputSize } from "./utils";

const enMonths = [
  {
    value: "01",
    label: "January"
  },
  {
    value: "02",
    label: "February"
  },
  {
    value: "03",
    label: "March"
  },
  {
    value: "04",
    label: "April"
  },
  {
    value: "05",
    label: "May"
  },
  {
    value: "06",
    label: "June"
  },
  {
    value: "07",
    label: "July"
  },
  {
    value: "08",
    label: "August"
  },
  {
    value: "09",
    label: "September"
  },
  {
    value: "10",
    label: "October"
  },
  {
    value: "11",
    label: "November"
  },
  {
    value: "12",
    label: "December"
  }
];

const esMonths = [
  {
    value: "01",
    label: "Enero"
  },
  {
    value: "02",
    label: "Febrero"
  },
  {
    value: "03",
    label: "Marzo"
  },
  {
    value: "04",
    label: "Abril"
  },
  {
    value: "05",
    label: "Mayo"
  },
  {
    value: "06",
    label: "Junio"
  },
  {
    value: "07",
    label: "Julio"
  },
  {
    value: "08",
    label: "Agosto"
  },
  {
    value: "09",
    label: "Septiembre"
  },
  {
    value: "10",
    label: "Octubre"
  },
  {
    value: "11",
    label: "Noviembre"
  },
  {
    value: "12",
    label: "Diciembre"
  }
];

const translatedPlaceholder = (locale: string) => {
  switch (locale) {
    case "es":
      return "Mes";
    case "en":
    default:
      return "Month";
  }
};

export const MonthSelect: FC<{
  error?: boolean;
  locale: string;
  size: InputSize;
  borderLess?: boolean;
  className?: string;
  readOnly?: boolean;
  onFocus?: () => void;
  value?: string;
  onChange: (_month: string) => void;
  menuPosition?: MenuPosition;
  menuPlacement?: MenuPlacement;
  searchable?: boolean;
  menuPortalTarget?: HTMLElement | null;

  legacyErrorMargins: boolean;
}> = ({
  error,
  className,
  size,
  value,
  onChange,
  readOnly,
  onFocus,
  locale,
  menuPosition,
  legacyErrorMargins,
  menuPlacement,
  searchable = true,
  menuPortalTarget
}) => {
  const months = useMemo(() => {
    switch (locale) {
      case "es":
        return esMonths;
      case "en":
      default:
        return enMonths;
    }
  }, [locale]);

  // `null`, not `undefined`: react-select falls back to its own internal state
  // when `value` is undefined, which would keep a stale month on screen after clearing.
  const activeMonth = months.find((m) => m.value === value) ?? null;

  // If mobile device -- use native select
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  ) {
    return (
      <select
        className={cx(
          "placeholder:text-content-100 block cursor-pointer rounded-md text-base placeholder:text-base placeholder:font-normal w-full min-w-[142px]",
          legacyErrorMargins ? "mb-2" : "",
          // focus outline
          "focus:border-primary-100 shadow-xs focus:shadow-focus-ring focus:outline-0 focus:ring-0",
          error ? "border-danger-500" : "border-alpha-100",
          readOnly ? "bg-alpha-40 text-content-100" : "",
          // For placeholder color
          !months.some((m) => m.value === value) ? "text-content-100" : "",
          className
        )}
        aria-invalid={Boolean(error && !activeMonth)}
        onChange={(e) => {
          if (!readOnly) {
            onChange(e.target.value);
          }
        }}
        onFocus={(e) => {
          e.currentTarget.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }}
        value={value || ""}
      >
        <option disabled value="">
          {translatedPlaceholder(locale)}
        </option>
        {months.map((month) => (
          <option key={month.value} value={month.value}>
            {month.label}
          </option>
        ))}
      </select>
    );
  }

  // data-lv-mask: the chosen month (e.g. of a date of birth) is applicant data,
  // hidden from BoomView live view like typed input.
  return (
    <div className="w-full min-w-[142px]" data-lv-mask="">
      <ReactSelect
        menuPosition={menuPosition}
        menuPlacement={menuPlacement}
        menuPortalTarget={menuPortalTarget}
        className={cx(
          "placeholder:text-content-100 block cursor-pointer rounded-md text-base placeholder:text-base placeholder:font-normal",
          legacyErrorMargins ? "mb-2" : "",
          error ? "border-danger-500" : "border-alpha-100",
          className
        )}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- need it
        // @ts-ignore -- need this
        error={error}
        isDisabled={readOnly}
        isMulti={false}
        isSearchable={searchable}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- need it
        // @ts-ignore -- need this
        onChange={(v: { value: string; label: string }) => {
          if (!readOnly) {
            onChange(v.value);
          }
        }}
        onFocus={() => {
          if (onFocus) {
            onFocus();
          }
        }}
        aria-invalid={Boolean(error && !activeMonth)}
        options={months as unknown as GroupBase<string>[]}
        placeholder={translatedPlaceholder(locale)}
        styles={sizedSelectStyles(size) as StylesConfig}
        theme={defaultSelectTheme}
        value={activeMonth}
      />
    </div>
  );
};
